import React, { useState, useEffect, useRef } from 'react';
import { Button, Container, IconButton, Typography, Box, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import '../../Calendar.css'; 
import MiniDrawer from '../../components/MiniDrawer.js';
import { useTheme } from '@mui/material/styles';
import useAlert from '../../alert/useAlert.js';
import AlertDialog from '../../Dialog.js';
import { Download as DownloadIcon, Visibility as ViewIcon } from "@mui/icons-material";
import UploadScreen from "./UploadScreen.js";
import SummaryViewer from './SummaryViewer.js';
import AddIcon from '@mui/icons-material/Add';
import SearchBar from '../../components/Search.js';
import axios from "axios";
import CryptoJS from 'crypto-js';
import { supabase } from '../../supabaseClient.js';
import { useSelector, shallowEqual } from 'react-redux';
import { format, set } from 'date-fns';
import CircularProgress from '@mui/material/CircularProgress';

const mockData = [
    { date: "2024-11-18", time: "10:00 AM", patient: "John Doe" },
    { date: "2024-11-18", time: "12:30 PM", patient: "Jane Smith" },
    { date: "2024-11-17", time: "03:15 PM", patient: "Emily Davis" },
];

const patientReportTemplate = `
Patient Information:

- [Patient Name, ID, and Date of Birth (mention if available)]

- [Referring Physician (mention if available)]

- [Date of Examination (mention if available)]

- [Type of Study with details such as imaging modality used, anatomical part of body studied, side of body studied, etc. (e.g. X-Ray Left Ankle - AP & Lat) (mention if available)]

 

Clinical History:

- [Brief description of the clinical indications or reasons for the imaging study, including specific symptoms or conditions being investigated (mention if available)]

 

Comparison:

- [Any prior imaging studies compared to the current study (mention if applicable and if available)]

 

Technique:

- [Details of the imaging technique and any contrast material used (mention if available)]

 

Findings:

- [Detailed description of findings one-by-one from the imaging study, organized by anatomical region or system. Describe abnormal findings, including location, size, characteristics of any lesions, masses, fractures, other abnormalities, etc. Include normal findings and variations (mention if available)]

- [Assessment of specific structures or areas of concern as per the clinical history or referral request (mention if available)]

 

Impression:

- [Summary of key findings, including potential diagnoses or conditions (include diagnoses only if explicitly mentioned)]

- [Recommendations for follow-up studies, further imaging, or clinical management based on the imaging findings (mention if applicable and if available)]

 

[Additional Notes:(mention only if applicable and if available)]

- [Any limitations of the study or factors affecting interpretation (mention only if applicable and if available)]

- [Comments on the quality of the study or any technical issues encountered (mention only if applicable and if available)]
`

const healthReportTemplate = `
Create a detailed, personalized health report based on laboratory test results and any relevant medical images provided. Please summarize the following information:

 

Patient demographic information (age, sex, relevant medical history)

Laboratory test results (provide normal ranges for context)

Medical imaging results (if applicable)

In your analysis:

 

Summarize overall test result of each test report in simple, understandable language

Only flag any values outside normal ranges and explain their significance. No discussion needed for values within normal ranges.

Provide context for why certain results might be concerning or reassuring

Suggest potential follow-up for healthcare provider

Recommend evidence-based lifestyle modifications relevant to the results

Include disclaimers that this analysis is not a medical diagnosis and should be reviewed with healthcare provider

Format the report with clear sections, including:

 

Executive summary with key findings

Detailed analysis of overall health conditions

Personalized recommendations

Follow-up considerations

Maintain a professional but accessible tone, avoiding overly alarming language while still clearly communicating any concerning findings.
`

const Summarizer = () => {
  const [openDialog, setOpenDialog] = useState({ open: false, content: '', confirmAction: null });
  const theme = useTheme();
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);

  const [view, setView] = useState("summaryList"); // Controls which view is active
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false); 

  const [prompt, setPrompt] = useState("");
  const [files, setFiles] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [formData, setFormData] = useState(null);
  // const [summary, setSummary] = useState("Medical Summary\nPatient Information:- Age/Gender: 65-year-old male Medical Procedures: - Coronary artery bypass grafting - Mitral valve repair Postoperative Course: - Experienced postoperative complications Discharge Status - Discharged in stable condition Additional Information - Introduction of BRATECA, a Brazilian Clinical Dataset with: - Over 70,000 admission - 2.5 million clinical notes - Aimed at improving non-English clinical data research and deidentification algorithms");
  const [summary, setSummary] = useState("");
  const [allSummaries, setAllSummaries] = useState([]);
  const [filteredAllSummaries, setFilteredAllSummaries] = useState([]);
  const [retrievedData, setRetrievedData] = useState([]);
  const [title, setTitle] = useState("");
  const { setAlert } = useAlert();
  const hasMounted = useRef(false);

  useEffect(() => {
    if (hasMounted.current) { return; }
    fetchSummaries();
    hasMounted.current = true;
  }, []);

  const fetchSummaries = async () => {
    setIsLoading(true); 
    try {
      let { data, error } = await supabase.from('summarizer').select('*').eq('teamid', currentOrg ? currentOrg.id : '');
      if (error) {
        console.error("Error fetching summaries:", error);
        return;
      }
      const updatedData = await Promise.all(data.map(async (summary) => {
        const fileList = await fetchSummarizerFiles(summary.title, summary.documents);
        return {
          ...summary,  // Correct spread syntax
          fileList: fileList
        };
      }));
      setAllSummaries(updatedData); 
      setFilteredAllSummaries(updatedData);
    } catch (err) {
      console.error("Error in fetch process:", err);
      setAlert("An error occurred while loading summaries", "error");
    } finally {
      setIsLoading(false); // Always reset loading state when done
    }
  };

  const fetchSummarizerFiles = async (title, documents) => {
    try {
      const filesFromStorage = [];
      
      for (const doc of documents) {
        const hashedFileName = hashFileName(doc.name);
        const fileExtension = doc.name.slice(doc.name.lastIndexOf("."));
        const finalFileName = `${hashedFileName}${fileExtension}`;
        
        const { data, error } = await supabase.storage
          .from('tikcare-attachments')
          .download(`${userDetails.id}/${title}/${finalFileName}`);
        if (error) {
          console.error('Error downloading file from Supabase:', error);
          continue;
        }
        const mimeType = getMimeType(doc.name);
        const file = new File([data], doc.name, { type: mimeType });
        filesFromStorage.push(file);
      }
      return filesFromStorage;
    } catch (error) {
      console.error('Error retrieving files from Supabase:', error);
      setAlert('Failed to retrieve files for text search', 'error');
      return;
    }
  }

  const getMimeType = (fileName) => {
    const extension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
    const mimeTypes = {
        pdf: 'application/pdf',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        txt: 'text/plain',
    };

    return mimeTypes[extension];
};

  const handleSummarySelect = (summary) => {
    setSelectedSummary(summary);
    setPrompt(summary.prompt);
    setFiles(summary.documents);
    setView("summaryViewer");
  };

  const handleSearchChange = (event) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchText(searchValue);
    setFilteredAllSummaries(allSummaries.filter((summary) => summary.title.toLowerCase().includes(searchValue)));
  };


  function hashFileName(fileName) {
      return CryptoJS.SHA256(fileName).toString(CryptoJS.enc.Hex);
  }

  const handleAddDocumentsSupabase = async () => {
      for (const file of fileList) {
          const hashedFileName = hashFileName(file.name);
          const fileExtension = file.name.slice(file.name.lastIndexOf("."));
          const finalFileName = `${hashedFileName}${fileExtension}`;
          const { error } = await supabase.storage.from('tikcare-attachments').upload(`${userDetails.id}/${title}/${finalFileName}`, file, {
              cacheControl: '3600',
              upsert: true,
          })
          if (error) {
              console.error('Error uploading file:', error);
              throw new Error('Error uploading file:', error);
          }
      }
  }

  const handleDeleteDocumentsSupabase = async (title, fileName) => {
    const hashedFileName = hashFileName(fileName);
    const fileExtension = fileName.slice(fileName.lastIndexOf("."));
    const finalFileName = `${hashedFileName}${fileExtension}`;

    const { error } = await supabase.storage.from('tikcare-attachments').remove([`${userDetails.id}/${title}/${finalFileName}`]);
    if (error) {
        console.error('Error deleting file:', error);
        throw new Error('Error deleting file:', error);
    } else {
      const updatedFiles = files.filter((file) => file.name !== fileName)
      await updateDocuments(updatedFiles);
      setFiles(updatedFiles);
      setSelectedSummary({ ...selectedSummary, fileList: selectedSummary.fileList.filter((file) => file.name !== fileName), documents: updatedFiles });
      setAlert('File deleted successfully!', 'success');
    }
  }

  const downloadFile = async (title, fileName) => {
    const hashedFileName = hashFileName(fileName);
    const fileExtension = fileName.slice(fileName.lastIndexOf("."));
    const finalFileName = `${hashedFileName}${fileExtension}`;

    const { data, error } = await supabase.storage.from('tikcare-attachments').download(`${userDetails.id}/${title}/${finalFileName}`);
    if (error) {
        console.error('Error downloading file:', error);
        setAlert('Failed to download the file!', 'error');
    } 
    else {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    }
  }

  const insertSummary = async (newSummary) => {
    const {error} = await supabase.from('summarizer').insert([{teamid: currentOrg ? currentOrg.id : null, summary: newSummary, title: title, prompt: prompt, documents: files}]);
    if (error) {
      console.error("Error inserting summary:", error);
      setAlert("Failed to insert summary", "error");
    }
  }

  const regenerateSelectedSummary = async (lengthOption, formatOption) => {
    const formData = new FormData();
    if(selectedSummary.fileList.length === 0) {
      setAlert("No files uploaded to generate a summary!", "error");
      setSelectedSummary({ ...selectedSummary, summary: "", prompt: prompt });
      await handleSaveSummary("", prompt);
      return;
    }
    selectedSummary.fileList.forEach((file) => {
      formData.append('files', file);
    });
    const generatedPrompt = lengthOption && formatOption
        ? `Generate a ${lengthOption} summary using ${formatOption}`
        : prompt;
    formData.append('prompt', generatedPrompt);
    setFormData(formData);

    try {
        const response = await axios.post(`${process.env.REACT_APP_MAIN_BACKEND_URL}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Access-Control-Allow-Origin': '*',
            },
        });
        const formattedSummary = response.data.summary.replace(/\*\*|###/g, '');
        await handleSaveSummary(formattedSummary, prompt);
        setSummary(formattedSummary);
        setSelectedSummary({ ...selectedSummary, summary: formattedSummary, prompt: prompt });
    } catch (error) {
        console.error('Error uploading files:', error);
    }
  }

  const uploadFiles = async (lengthOption, formatOption) => {
    if (!fileList.length) {
        alert("Please select files first!");
        return;
    }
    const formData = new FormData();
    Array.from(fileList).forEach((file) => {
      formData.append('files', file);
    });
    const generatedPrompt = lengthOption && formatOption
        ? `Generate a ${lengthOption} summary using ${formatOption}`
        : prompt;
    formData.append('prompt', generatedPrompt);
    setFormData(formData);

    try {
        const response = await axios.post(`${process.env.REACT_APP_MAIN_BACKEND_URL}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Access-Control-Allow-Origin': '*',
            },
        });
        const formattedSummary = response.data.summary.replace(/\*\*|###/g, '');
        await handleAddDocumentsSupabase();
        await insertSummary(formattedSummary);
        setSummary(formattedSummary);
        setSelectedSummary({summary: formattedSummary, title: title, prompt: prompt, documents: files, fileList: fileList});
    } catch (error) {
        console.error('Error uploading files:', error);
    }
  };


  const handleRetrieveText = async (highlightedText) => {
    const formData = new FormData();
    selectedSummary.fileList.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('notes', '');
    formData.append('highlighted_text', highlightedText);
    setFormData(formData);
    try {
        const response = await axios.post(`${process.env.REACT_APP_MAIN_BACKEND_URL}/retrieve`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Access-Control-Allow-Origin': '*',
            },
        });
        let data;
        if (typeof response.data.source_text === "string") {
          data = JSON.parse(response.data.source_text);
        } else {
          data = response.data.source_text;
        }
        setRetrievedData(data);
        return data;
    } catch (error) {
        console.error("Error retrieving text:", error);
    }
  };

  const clearFields = () => {
    setSelectedSummary(null);
    setPrompt("");
    setFiles([]);
    setFormData(null);
    setSummary("");
    setFileList([]);
    setTitle("");
  }

  const handlePromptSelector = (option) => {
    if(option === 'patientReportTemplate') {
      setPrompt(patientReportTemplate);
    } else if(option === 'healthReportTemplate') {
      setPrompt(healthReportTemplate);
    }
  }

  const handleCopySummary = (summary) => {
    navigator.clipboard.writeText(summary);
    setAlert('Summary copied to clipboard!', 'success');
  }

  const handleSaveSummary = async (summary, prompt) => {
    const {error} = await supabase.from('summarizer').update({summary: summary, prompt: prompt}).eq('teamid', currentOrg.id).eq('title', selectedSummary.title);
    if (error) {
      console.error("Error updating summary:", error);
      setAlert("Failed to save summary", "error");
    } else {
      if(selectedSummary.fileList.length > 0) {
        setAlert('Summary saved successfully!', 'success');
      }
    }
  }

  const updateDocuments = async (files) => {
    const {error} = await supabase.from('summarizer').update({documents: files}).eq('teamid', currentOrg.id).eq('title', selectedSummary.title);
    if (error) {
      console.error("Error updating documents:", error);
      setAlert("Failed to update documents", "error");
    } 
  }

  return (
    <Container disableGutters maxWidth={false}>
      <Grid container sx={{overflow: 'auto', minHeight: '100vh', minWidth: '100vw'}}>
        <Grid size={{xs: 1, md: 0}}>
          <MiniDrawer />
        </Grid>
        <Grid size="grow">
          <Grid container spacing={5} sx={{ backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column',  width: '100%', height: '100%' }}>
            <Grid size={12}>
              <div style={{ padding: '24px 10vw' }}>
                <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
                    {view === "summaryList" && (
                        <>
                        <Grid
                            sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mb: 2 }}
                        >
                            <Typography variant="h4" sx={{marginRight: '20px'}}>All Summaries</Typography>
                            <Button onClick={() => { setView("documentUpload"); clearFields(); }} variant="outlined" startIcon={<AddIcon sx={{color: '#fff'}}/>} sx={{marginRight: '20px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                              <Typography variant="button" sx={{color: '#FFFFFF'}}>New Summary</Typography>
                            </Button>
                        </Grid>

                        <Grid sx={{ mb: 2 }}>
                          <SearchBar bgcolor='#FFFFFF' placeholder="Search using a summary title" width="30vw" value={searchText} onChange={handleSearchChange}/>
                        </Grid>

                        {isLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <div>                    
                          <Box sx={{ backgroundColor: '#ffffff', padding: '16px', marginBottom: '16px', borderRadius: '16px 16px 0 0',}}>
                            <Grid container spacing={2} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                              <Grid size={6} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                <Typography noWrap variant="body2" sx={{fontWeight: 'bold'}}>Date/Time</Typography>
                              </Grid>
                              <Grid size={6} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                <Typography noWrap variant="body2" sx={{fontWeight: 'bold'}}>Title</Typography>
                              </Grid>
                              {/* <Grid size={2} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                <Typography noWrap variant="body2" sx={{fontWeight: 'bold'}}>Download</Typography>
                              </Grid>
                              <Grid size={2} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                <Typography noWrap variant="body2" sx={{fontWeight: 'bold'}}>View</Typography>
                              </Grid> */}
                            </Grid>
                          </Box>
                  
                          <Box sx={{ maxHeight: '60vh', overflowY: 'auto'}}>
                            {filteredAllSummaries.map((summary, index) => (
                              <Box onClick={() => handleSummarySelect(summary) } key={index} sx={{ '&:hover': { border: "3px solid #29C0BB" }, backgroundColor: '#ffffff', padding: '16px', marginBottom: '8px', cursor: 'pointer' }}>
                                <Grid container spacing={2} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                  <Grid size={6} sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center'}}>
                                  <Typography variant="body2" >
                                    {format(summary.created_at, 'dd MMM, yyyy')}
                                  </Typography>
                                  </Grid>
                                  <Grid size={6} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <Typography variant="body2" sx={{textAlign: 'center'}}>{summary.title}</Typography>
                                  </Grid>
                                  {/* <Grid size={2} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <Tooltip title="Download Summary">
                                      <IconButton onClick={(e) => { e.stopPropagation(); }} sx={{backgroundColor: '#f5f5f5', borderRadius: '8px', width: '40px', height: '40px'}} >
                                        <DownloadIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid>
                                  <Grid size={2} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <Tooltip title="View Summary">
                                      <IconButton onClick={(e) => { e.stopPropagation(); }} sx={{backgroundColor: '#f5f5f5', borderRadius: '8px', width: '40px', height: '40px'}} >
                                        <ViewIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid> */}
                                </Grid>
                              </Box>
                            ))}
                            </Box>
                         </div>
                        )}
                        </>
                    )}
                    {view === "documentUpload" && (
                        <UploadScreen
                        onBack={() => setView("summaryList")}
                        onProceed={() => {setView("summaryViewer");}} 
                        prompt={prompt}
                        setPrompt={setPrompt}
                        files={files}
                        setFiles={setFiles}
                        setFormData={setFormData}
                        setFileList={setFileList}
                        setTitle={setTitle}
                        title={title}
                        handlePromptSelector={handlePromptSelector}
                        />
                    )}

                    {/* Summary Viewer View */}
                    {view === "summaryViewer" && (
                        <SummaryViewer
                        selectedSummary={selectedSummary}
                        setSelectedSummary={setSelectedSummary}
                        onBack={() => { setView("summaryList"); fetchSummaries(); }}
                        prompt={prompt}
                        setPrompt={setPrompt}
                        files={files}
                        uploadFiles={uploadFiles}
                        handleRetrieveText={handleRetrieveText}
                        retrievedData={retrievedData}
                        downloadFile={downloadFile}
                        handleDeleteDocumentsSupabase={handleDeleteDocumentsSupabase}
                        handlePromptSelector={handlePromptSelector}
                        handleCopySummary={handleCopySummary}
                        handleSaveSummary={handleSaveSummary}
                        regenerateSelectedSummary={regenerateSelectedSummary}
                        />
                    )}
                    </Grid>
                  {
                    openDialog && (
                      <AlertDialog data={{open: openDialog.open, content: openDialog.content, confirmAction: openDialog.confirmAction, setOpen: setOpenDialog}}/>
                    )
                  }
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Summarizer;
